/* eslint-disable @typescript-eslint/no-explicit-any */
// Small geo helpers around the area shapes module: a GeoJSON export and a flat-earth area estimate.
import { readShape } from "../geo/areaShapes";
import type { AreaShape } from "../geo/areaShapes";

type Coord = [number, number];
type Ring = Coord[];

export interface ShapeFeatureInput {
  id?: string | number;
  geometry: AreaShape;
  properties?: Record<string, any>;
}

/** A GeoJSON FeatureCollection of the shapes, for download. */
export function shapesToFeatureCollection(items: ShapeFeatureInput[]): any {
  const features = items.map((item) => ({
    type: "Feature",
    ...(item.id !== undefined ? { id: item.id } : {}),
    properties: item.properties ?? (item.id !== undefined ? { id: item.id } : {}),
    geometry: item.geometry,
  }));

  return { type: "FeatureCollection", features };
}

export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/geo+json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".geojson") ? filename : `${filename}.geojson`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function safeFilename(name: string): string {
  return (
    name
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "export"
  );
}

const KM_PER_DEG = 111.32;

/** Area of a WKT polygon or multipolygon in km² (flat-earth estimate, holes subtracted); 0 for anything else. */
export function wktAreaKm2(wkt: string): number {
  const geom = readShape(wkt);
  if (!geom || (geom.type !== "Polygon" && geom.type !== "MultiPolygon")) return 0;
  const polygons: Ring[][] = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
  const ringKm2 = (ring: Ring): number => {
    if (ring.length < 3) return 0;
    const lat0 = (ring.reduce((s, c) => s + c[1], 0) / ring.length) * (Math.PI / 180);
    const kx = KM_PER_DEG * Math.cos(lat0);
    let sum = 0;
    for (let i = 0; i < ring.length; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[(i + 1) % ring.length];
      sum += x1 * kx * (y2 * KM_PER_DEG) - x2 * kx * (y1 * KM_PER_DEG);
    }
    return Math.abs(sum) / 2;
  };
  let total = 0;
  for (const rings of polygons) {
    for (let i = 0; i < rings.length; i++) total += i === 0 ? ringKm2(rings[i]) : -ringKm2(rings[i]);
  }
  return Math.max(0, total);
}
